import { isFuture } from 'date-fns';
import { ReactComponent as SelfAssessmentIcon } from '../assets/icons/self-assesment-small.svg';
import { ReactComponent as DirectReviewIcon } from '../assets/icons/direct-review-small.svg';
import { ReactComponent as TeamReviewIcon } from '../assets/icons/team-review-small.svg';
import { ReactComponent as PeerReviewIcon } from '../assets/icons/peer-review-small.svg';
import { ReactComponent as CompanyPulseIcon } from '../assets/icons/company-pulse-small.svg';
import { ReactComponent as EnpsIcon } from '../assets/icons/pie_chart.svg';
import { ReactComponent as AnonymousIcon } from '../assets/icons/anonymous-user.svg';
import { ReactComponent as OneTimeSurveyIcon } from '../assets/icons/excluded.svg';
import { ReactComponent as OpenEndedIcon } from '../assets/icons/text_snippet.svg';
import { ReactComponent as MultipleChoiceIcon } from '../assets/icons/radio_button_checked.svg';
import { ReactComponent as MultipleSelectIcon } from '../assets/icons/check-box-checked.svg';
import { ReactComponent as RankingIcon } from '../assets/icons/reorder.svg';
import {
  forbiddenCharacters,
  isEmpty,
  validateLength,
} from '../utility/validation';
import {
  prepareSurveyTitle,
  validateQestionnaireQuestions,
} from '../utility/survey';
import { isArrayEmpty, trimString } from '../utility/helpers';
import http from '../utility/http';
import {
  addNumberOfDays,
  substractNumberOfDays,
  checkIsSameDay,
  addHoursToCurrentStartHour,
  getSurveyDurationDays,
  formatDate,
} from '../utility/dateUtils';
import { FIELD_TYPES } from './fieldTypes';
import { API_SURVEY_GROUPS_BASIC } from './apiRoutes';

const {
  TEXT,
  SELECT,
  SURVEY_PREVIEW,
  CHECKBOX,
  DATE_PICKER,
  TIME_PICKER,
  READ_ONLY,
  PEOPLE_PICKER,
  SWITCH,
  SUBTITLE,
  QUESTIONNAIRE_QUESTIONS,
} = FIELD_TYPES;

export const QUESTIONNAIRE_QUESTION_DEFAULT_OPTIONS_COUNT = 4;
export const QUESTIONNAIRE_QUESTION_MIN_OPTIONS = 2;
export const QUESTIONNAIRE_QUESTION_MAX_OPTIONS = 15;

export const EMPTY_QUESTIONNAIRE = { name: '', description: '', questions: [] };

export const CREATE_SURVEY_FIELD_NAMES = {
  CREATED_FOR: 'created_for',
  IS_ANONYMOUS: 'is_anonymous',
  ATTRIBUTES: 'attributes',
  ATTRIBUTES_WITH_COMMENT: 'attributes_with_answer_comment',
  TITLE: 'title',
  REVIEWERS: 'surveyee',
  IS_SCHEDULE: 'isSchedule',
  SCHEDULED_FOR: 'schedule_for',
  FILL_INLINE: 'fill_inline',
  SURVEY_TYPE: 'survey_type',
  CLIENT_URL: 'client_url',
  IS_STATISTICAL: 'is_statistical',
  EXPIRATION_DATE: 'expire_at',
  HAS_EMAIL_MESSAGE: 'hasEmailMessage',
  HAS_AUTO_SHARE: 'hasAutoShare',
  SURVEY_SUMMARY: 'survey_summary_data',
  SHARE_TO: 'share_to',
  DESCRIPTION: 'description',
  EMAIL_MESSAGE: 'message',
  GROUP: 'group',
  STATUS: 'status',
  SEND: 'send',
  HAS_AUTO_REMINDER: 'hasAutoReminder',
  AUTO_REMINDERS: 'auto_reminders',
  HAS_QUESTIONNAIRE: 'hasQuestionnaire',
  QUESTIONNAIRE: 'questionnaire',
};

export const AUTO_REMINDER_OPTION_IDS = {
  HALFWAY: 'halfway',
  DAY_BEFORE_END: 'day_before_end',
  DAYS_BEFORE_3: 'days_before_3',
  DAYS_BEFORE_7: 'days_before_7',
};

export const QUESTIONNAIRE_QUESTION_TYPES = {
  OPEN_TEXT: 'OPEN_TEXT',
  SINGLE_SELECT: 'SINGLE_SELECT',
  MULTI_SELECT: 'MULTI_SELECT',
  RANKING: 'RANKING',
};

export const QUESTIONNAIRE_QUESTION_ANSWER_TYPES = {
  OPEN_TEXT: 'text_value',
  SINGLE_SELECT: 'selected_option_ids',
  MULTI_SELECT: 'selected_option_ids',
  RANKING: 'ranked_option_ids',
};

export const QUESTIONNAIRE_QUESTION_OPTIONS = [
  {
    id: QUESTIONNAIRE_QUESTION_TYPES.SINGLE_SELECT,
    label: 'Multiple choice',
    icon: MultipleChoiceIcon,
    translationKey: 'multipleChoice',
  },
  {
    id: QUESTIONNAIRE_QUESTION_TYPES.MULTI_SELECT,
    label: 'Multi select',
    icon: MultipleSelectIcon,
    translationKey: 'multiSelect',
  },
  {
    id: QUESTIONNAIRE_QUESTION_TYPES.RANKING,
    label: 'Ranking',
    icon: RankingIcon,
    translationKey: 'ranking',
  },
  {
    id: QUESTIONNAIRE_QUESTION_TYPES.OPEN_TEXT,
    label: 'Open-ended',
    icon: OpenEndedIcon,
    translationKey: 'openEnded',
  },
];

export const TITLE_FIELD = {
  required: true,
  validators: [
    { type: 'isEmpty', validator: isEmpty },
    { type: 'forbiddenCharacters', validator: forbiddenCharacters },
    {
      type: 'maxEntryLength',
      validator: value => validateLength(value, 0, 500),
    },
  ],
};

export const AUTO_REMINDER_DISPLAY_FORMAT = 'dd MMM y, hh:mm a';

const formatAutoReminderDate = date => new Date(date).toISOString();

const getHalfwayReminderDate = (startDate, expireDate) => {
  const start = new Date(startDate);
  const expire = new Date(expireDate);
  const midpoint = new Date(
    start.getTime() + (expire.getTime() - start.getTime()) / 2
  );

  return new Date(
    Date.UTC(
      midpoint.getUTCFullYear(),
      midpoint.getUTCMonth(),
      midpoint.getUTCDate(),
      expire.getUTCHours(),
      expire.getUTCMinutes(),
      expire.getUTCSeconds(),
      0
    )
  );
};

export const getAutoReminderOptionLabels = translations =>
  translations?.formLabels?.auto_reminders?.options || {};

export const getAutoReminderOptions = (
  startDate,
  expireDate,
  optionLabels = {}
) => {
  const durationDays = getSurveyDurationDays(startDate, expireDate);
  const expire = new Date(expireDate);
  const { HALFWAY, DAYS_BEFORE_7, DAYS_BEFORE_3, DAY_BEFORE_END } =
    AUTO_REMINDER_OPTION_IDS;

  return [
    {
      id: HALFWAY,
      date: formatAutoReminderDate(
        getHalfwayReminderDate(startDate, expireDate)
      ),
      label: optionLabels[HALFWAY],
      isDisabled: false,
    },
    {
      id: DAYS_BEFORE_7,
      date: formatAutoReminderDate(substractNumberOfDays(expire, 7)),
      label: optionLabels[DAYS_BEFORE_7],
      isDisabled: durationDays < 8,
    },
    {
      id: DAYS_BEFORE_3,
      date: formatAutoReminderDate(substractNumberOfDays(expire, 3)),
      label: optionLabels[DAYS_BEFORE_3],
      isDisabled: durationDays < 4,
    },
    {
      id: DAY_BEFORE_END,
      date: formatAutoReminderDate(substractNumberOfDays(expire, 1)),
      label: optionLabels[DAY_BEFORE_END],
      isDisabled: false,
    },
  ];
};

export const getAutoReminderFormFields = (
  startDate,
  expireDate,
  translations,
  customRootClass
) => {
  const optionLabels = getAutoReminderOptionLabels(translations);

  return [
    {
      name: CREATE_SURVEY_FIELD_NAMES.AUTO_REMINDERS,
      type: FIELD_TYPES.CHECKBOX_GROUP,
      customRootClass,
      syncByKey: 'date',
      getItems: () =>
        getAutoReminderOptions(startDate, expireDate, optionLabels).map(
          option => ({
            ...option,
            label: `${option.label} (${formatDate(
              option.date,
              AUTO_REMINDER_DISPLAY_FORMAT
            )})`,
          })
        ),
    },
  ];
};

export const normalizeAutoReminderDate = date =>
  date ? formatAutoReminderDate(date) : date;

export const mapAutoReminderDatesToKeys = (selected, startDate, expireDate) => {
  const selectedDates = (selected || []).map(normalizeAutoReminderDate);

  return getAutoReminderOptions(startDate, expireDate)
    .filter(option => !option.isDisabled && selectedDates.includes(option.date))
    .map(option => option.id);
};

export const mapAutoReminderKeysToDates = (selected, startDate, expireDate) => {
  const selectedKeys = selected || [];

  return [
    ...new Set(
      getAutoReminderOptions(startDate, expireDate)
        .filter(
          option => !option.isDisabled && selectedKeys.includes(option.id)
        )
        .map(option => option.date)
    ),
  ];
};

export const pruneAutoReminderKeys = (selectedKeys, startDate, expireDate) =>
  getAutoReminderOptions(startDate, expireDate)
    .filter(
      option => !option.isDisabled && (selectedKeys || []).includes(option.id)
    )
    .map(option => option.id);

export const pruneAutoReminders = (selected, startDate, expireDate) =>
  mapAutoReminderKeysToDates(selected, startDate, expireDate);

export const remountAutoReminders = (
  selected,
  previousStartDate,
  previousExpireDate,
  nextStartDate,
  nextExpireDate
) => {
  const selectedKeys = mapAutoReminderDatesToKeys(
    selected,
    previousStartDate,
    previousExpireDate
  );

  return mapAutoReminderKeysToDates(
    selectedKeys,
    nextStartDate,
    nextExpireDate
  );
};

export const getAutoReminderDisplayItems = (
  selected,
  startDate,
  expireDate,
  translations
) => {
  const selectedReminders = (selected || [])
    .map(normalizeAutoReminderDate)
    .filter(Boolean);

  if (!selectedReminders.length) {
    return [];
  }

  const optionLabels = getAutoReminderOptionLabels(translations);

  return getAutoReminderOptions(startDate, expireDate, optionLabels)
    .filter(option => selectedReminders.includes(option.date))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map((option, index, items) => ({
      id: option.id,
      date: formatDate(option.date, AUTO_REMINDER_DISPLAY_FORMAT),
      label: option.label,
      hasSuffix: index < items.length - 1,
    }));
};

export const prepareInitialAutoReminderData = (
  autoReminders,
  startDate,
  expireDate
) => ({
  [CREATE_SURVEY_FIELD_NAMES.AUTO_REMINDERS]: mapAutoReminderDatesToKeys(
    autoReminders || [],
    startDate,
    expireDate
  ),
});

export const getCreateSurveyAutoReminderStartDate = values => {
  const { IS_SCHEDULE, SCHEDULED_FOR } = CREATE_SURVEY_FIELD_NAMES;

  return values[IS_SCHEDULE] ? values[SCHEDULED_FOR] : new Date();
};

export const getCreateSurveyAutoReminderItems = (values, formLabels) => {
  const optionLabels = formLabels?.auto_reminders?.options || {};

  return getAutoReminderOptions(
    getCreateSurveyAutoReminderStartDate(values),
    values[CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE],
    optionLabels
  ).map(option => ({
    ...option,
    label: `${option.label} (${formatDate(
      option.date,
      AUTO_REMINDER_DISPLAY_FORMAT
    )})`,
  }));
};

export const remountCreateSurveyAutoReminders = (
  selected,
  _previousValues,
  nextValues
) => {
  const { EXPIRATION_DATE, HAS_AUTO_REMINDER } = CREATE_SURVEY_FIELD_NAMES;

  if (!nextValues[HAS_AUTO_REMINDER]) {
    return selected || [];
  }

  return pruneAutoReminderKeys(
    selected,
    getCreateSurveyAutoReminderStartDate(nextValues),
    nextValues[EXPIRATION_DATE]
  );
};

const getCreateSurveyAutoReminderRemountDependant = changedFieldName => ({
  name: CREATE_SURVEY_FIELD_NAMES.AUTO_REMINDERS,
  resetFnc: (
    fieldValue,
    currentFieldValue,
    dependantValue,
    initialValues,
    values
  ) => {
    const nextValues = {
      ...values,
      [changedFieldName]: currentFieldValue,
    };

    return remountCreateSurveyAutoReminders(dependantValue, values, nextValues);
  },
});

export const SURVEY_TYPES = {
  SELF: 'SELF',
  TEAM: 'TEAM',
  PEER: 'PEER',
  DIRECT: 'DIRECT',
  PULSE: 'PULSE',
  NPS_ENPS: 'NPS_ENPS',
  NPS: 'NPS',
  ENPS: 'ENPS',
};

const MIN_REVIWERS_FOR_ANONYMOUS_SURVEY = 6;

export const getExpiryDialogFields = minDate => [
  {
    name: 'expiryDate',
    type: FIELD_TYPES.DATE_PICKER,
    translationKey: 'expiry',
    hasDivider: true,
    isToolbarDisabled: true,
    getMinDate: () => addNumberOfDays(new Date(minDate), 2),
  },
  {
    name: 'expiryTime',
    type: FIELD_TYPES.TIME_PICKER,
    hasDivider: true,
    isPastDisabled: true,
  },
];

const getMutualColumn = (
  hasAnonymousField = false,
  hasStatisticsField = true
) => ({
  id: 1,
  title: 'Survey',
  translationKey: 'survey',
  columnContentWrapperClass: 'mutualColumnContent',
  columnClass: 'mutualColumn',
  scrollYClass: 'mutualColumnScrollY',
  fields: [
    {
      name: CREATE_SURVEY_FIELD_NAMES.SURVEY_TYPE,
      type: SELECT,
      translationKey: 'surveyType',
      fieldWrapperClass: 'type',
      optionsKey: 'surveyTypes',
      required: true,
      isSearchDisabled: true,
      shouldResetOnChange: true,
      parser: {
        value: 'id',
        label: 'name',
      },
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.TITLE,
      type: TEXT,
      required: true,
      fieldWrapperClass: 'title',
      validators: [
        { type: 'required', validator: isEmpty },
        { type: 'forbiddenCharacters', validator: forbiddenCharacters },
        {
          type: 'maxLength',
          validator: value => validateLength(value, 0, 150),
        },
      ],
    },
    ...(hasAnonymousField
      ? [
          {
            name: CREATE_SURVEY_FIELD_NAMES.IS_ANONYMOUS,
            type: CHECKBOX,
            endLabelIcon: AnonymousIcon,
            translationKey: 'isAnonymous',
            fieldWrapperClass: 'anonymous',
            isControlled: true,
            smallText: true,
            isDisabledCheck: values =>
              values[CREATE_SURVEY_FIELD_NAMES.REVIEWERS].length <
                MIN_REVIWERS_FOR_ANONYMOUS_SURVEY ||
              values[CREATE_SURVEY_FIELD_NAMES.SURVEY_TYPE] ===
                SURVEY_TYPES.ENPS,
          },
        ]
      : []),
    ...(hasStatisticsField
      ? [
          {
            name: CREATE_SURVEY_FIELD_NAMES.IS_STATISTICAL,
            type: CHECKBOX,
            endLabelIcon: OneTimeSurveyIcon,
            translationKey: 'oneTime',
            fieldWrapperClass: 'statistical',
            isControlled: true,
            hasDivider: true,
            isReverse: true,
            smallText: true,
            isDisabledCheck: values =>
              !!values?.[CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE],
          },
        ]
      : []),
    {
      name: CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE,
      type: DATE_PICKER,
      translationKey: 'expiry',
      fieldWrapperClass: 'expiryDate',
      hasDivider: true,
      isToolbarDisabled: true,
      getMinDate: (value, values, initialValues) => {
        const isSchedule = values[CREATE_SURVEY_FIELD_NAMES.IS_SCHEDULE];
        const scheduledFor = values[CREATE_SURVEY_FIELD_NAMES.SCHEDULED_FOR];
        const initialScheduledFor =
          initialValues[CREATE_SURVEY_FIELD_NAMES.SCHEDULED_FOR];
        const isSameDay = checkIsSameDay(initialScheduledFor, scheduledFor);

        return isSchedule && !isSameDay
          ? addNumberOfDays(new Date(scheduledFor), 2)
          : addNumberOfDays(new Date(), 2);
      },
      validators: [{ type: 'isPast', validator: value => isFuture(value) }],
      dependants: [
        getCreateSurveyAutoReminderRemountDependant(
          CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE
        ),
      ],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE,
      type: TIME_PICKER,
      fieldWrapperClass: 'expiryTime',
      customRootClass: 'expiryTimeRoot',
      hasDivider: true,
      validators: [{ type: 'isPast', validator: value => isFuture(value) }],
      dependants: [
        getCreateSurveyAutoReminderRemountDependant(
          CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE
        ),
      ],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.HAS_EMAIL_MESSAGE,
      type: SWITCH,
      translationKey: 'email',
      fieldWrapperClass: 'email',
      isHighlightedLabel: true,
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.EMAIL_MESSAGE,
      type: TEXT,
      translationKey: 'emailContent',
      fieldWrapperClass: 'emailDescription',
      multiline: true,
      rows: 4,
      isToggleable: true,
      hasDivider: true,
      shouldRender: values =>
        values[CREATE_SURVEY_FIELD_NAMES.HAS_EMAIL_MESSAGE],
      validators: [
        { type: 'required', validator: isEmpty },
        { type: 'forbiddenCharacters', validator: forbiddenCharacters },
        {
          type: 'emailContentMaxLength',
          validator: value => validateLength(value, 0, 500),
        },
      ],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.IS_SCHEDULE,
      type: SWITCH,
      translationKey: 'schedule',
      fieldWrapperClass: 'schedule',
      isHighlightedLabel: true,
      dependants: [
        {
          name: CREATE_SURVEY_FIELD_NAMES.SCHEDULED_FOR,
          resetFnc: () => addHoursToCurrentStartHour(),
        },
        getCreateSurveyAutoReminderRemountDependant(
          CREATE_SURVEY_FIELD_NAMES.IS_SCHEDULE
        ),
      ],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.SCHEDULED_FOR,
      type: DATE_PICKER,
      fieldWrapperClass: 'scheduleDate',
      isToggleable: true,
      hasDivider: true,
      isToolbarDisabled: true,
      isPastDisabled: true,
      getMaxDate: (value, values) =>
        substractNumberOfDays(
          new Date(values[CREATE_SURVEY_FIELD_NAMES.EXPIRATION_DATE]),
          2
        ),
      shouldRender: values => values[CREATE_SURVEY_FIELD_NAMES.IS_SCHEDULE],
      validators: [
        {
          type: 'isPast',
          validator: (value, values) =>
            isFuture(value) || !values[CREATE_SURVEY_FIELD_NAMES.IS_SCHEDULE],
          shouldPassFormValues: true,
        },
      ],
      dependants: [
        getCreateSurveyAutoReminderRemountDependant(
          CREATE_SURVEY_FIELD_NAMES.SCHEDULED_FOR
        ),
      ],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.SCHEDULED_FOR,
      type: TIME_PICKER,
      translationKey: 'scheduledFor',
      fieldWrapperClass: 'scheduleTime',
      customRootClass: 'scheduleTimeRoot',
      isToggleable: true,
      hasDivider: true,
      shouldRender: values => values[CREATE_SURVEY_FIELD_NAMES.IS_SCHEDULE],
      validators: [
        {
          type: 'isPast',
          validator: (value, values) =>
            isFuture(value) || !values[CREATE_SURVEY_FIELD_NAMES.IS_SCHEDULE],
          shouldPassFormValues: true,
        },
      ],
      dependants: [
        getCreateSurveyAutoReminderRemountDependant(
          CREATE_SURVEY_FIELD_NAMES.SCHEDULED_FOR
        ),
      ],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.HAS_AUTO_REMINDER,
      type: SWITCH,
      translationKey: 'autoReminder',
      fieldWrapperClass: 'autoReminder',
      isHighlightedLabel: true,
      dependants: [
        {
          name: CREATE_SURVEY_FIELD_NAMES.AUTO_REMINDERS,
          resetFnc: (_, __, dependantValue, ___, values) => {
            if (!values[CREATE_SURVEY_FIELD_NAMES.HAS_AUTO_REMINDER]) {
              return [];
            }

            if (dependantValue?.length) {
              return dependantValue;
            }

            return [AUTO_REMINDER_OPTION_IDS.DAY_BEFORE_END];
          },
        },
      ],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.AUTO_REMINDERS,
      type: FIELD_TYPES.CHECKBOX_GROUP,
      translationKey: 'auto_reminders',
      fieldWrapperClass: 'autoReminderOptions',
      customRootClass: 'autoReminderCheckbox',
      syncByKey: 'date',
      isToggleable: true,
      shouldRender: values =>
        values[CREATE_SURVEY_FIELD_NAMES.HAS_AUTO_REMINDER],
      getItems: getCreateSurveyAutoReminderItems,
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.HAS_AUTO_SHARE,
      type: SWITCH,
      translationKey: 'autoShare',
      fieldWrapperClass: 'autoShare',
      isHighlightedLabel: true,
      dependants: [
        {
          name: CREATE_SURVEY_FIELD_NAMES.SHARE_TO,
          resetFnc: (_, __, dependantValue, ___, values) => {
            if (!values[CREATE_SURVEY_FIELD_NAMES.HAS_AUTO_SHARE]) {
              return [];
            }

            return dependantValue;
          },
        },
      ],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.DESCRIPTION,
      type: FIELD_TYPES.RICH_TEXT_EDITOR,
      fieldWrapperClass: 'summary',
      translationKey: 'summary',
      shouldRender: values => values[CREATE_SURVEY_FIELD_NAMES.HAS_AUTO_SHARE],
      isToggleable: true,
      validators: [
        {
          type: 'summaryMaxLength',
          validator: fieldValue => validateLength(fieldValue, 0, 20000),
        },
      ],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.SHARE_TO,
      type: PEOPLE_PICKER,
      translationKey: 'autoShareTo',
      selectedUsersScrollYClass: 'selectedUsersScrollY',
      fieldWrapperClass: 'autoShareTo',
      optionsKey: 'employees',
      hasSelectAll: true,
      showDirectReports: true,
      showSelection: true,
      shouldRender: values => values[CREATE_SURVEY_FIELD_NAMES.HAS_AUTO_SHARE],
      isToggleable: true,
      validators: [
        {
          type: 'required',
          shouldPassFormValues: true,
          validator: (fieldValue, values) =>
            values[CREATE_SURVEY_FIELD_NAMES.HAS_AUTO_SHARE]
              ? !isArrayEmpty(fieldValue)
              : true,
        },
      ],
    },
  ],
});

export const QUESTIONNAIRE_NAME_VALIDATORS = [
  { type: 'isEmpty', validator: isEmpty },
  { type: 'forbiddenCharacters', validator: forbiddenCharacters },
  {
    type: 'maxLength',
    validator: value => validateLength(value, 0, 150),
  },
];

export const QUESTIONNAIRE_DESCRIPTION_VALIDATORS = [
  {
    type: 'descriptionMaxLength',
    validator: value => validateLength(value || '', 0, 10000),
  },
];

const PREVIEW_COLUMN = {
  id: 3,
  title: 'Preview',
  translationKey: 'preview',
  columnScrollWrapperClass: values =>
    values?.[CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE]
      ? 'preview-wrapper-custom'
      : 'preview-wrapper-attributes',
  columnContentWrapperClass: values =>
    values?.[CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE]
      ? 'preview-column-contentWrapper-custom'
      : 'preview-column-contentWrapper-attributes',
  columnClass: 'preview-column',
  getTitle: (values, title) =>
    values?.[CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE] ? null : title,
  fields: [
    {
      name: 'survey_preview',
      translationKey: 'surveyPreview',
      type: SURVEY_PREVIEW,
      reviewKey: 'attribute',
      getReviewKey: 'getAttribute',
      clearReviewKey: 'clearAttribute',
      isToggleable: true,
      shouldFadeOutInstant: true,
      shouldRender: values =>
        !values?.[CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE],
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.QUESTIONNAIRE,
      translationKey: 'customQuestionnaire',
      type: QUESTIONNAIRE_QUESTIONS,
      isToggleable: true,
      shouldFadeOutInstant: true,
      shouldRender: values =>
        !!values?.[CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE],
      validators: [
        {
          type: 'required',
          isMultiple: true,
          shouldPassFormValues: true,
          validator: validateQestionnaireQuestions,
        },
      ],
    },
  ],
};

const prepareAttributesSelectField = () => ({
  name: CREATE_SURVEY_FIELD_NAMES.ATTRIBUTES,
  type: SELECT,
  optionsKey: 'attributesWithQuestions',
  fieldWrapperClass: 'attributes',
  required: true,
  isAttribute: true,
  hasAttributeComments: true,
  shouldReturnOption: true,
  shouldDisableSort: true,
  isAttributeDraggable: true,
  isMulti: true,
  hasColorBox: true,
  addCommentsFieldName: 'has_answer_comment',
  parser: {
    value: 'id',
    label: 'name',
  },
  isDisabledCheck: values =>
    !!values?.[CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE],
  dependants: [
    {
      name: CREATE_SURVEY_FIELD_NAMES.TITLE,
      resetFnc: (prevValue, currentValue, dependantValue, initialValues) => {
        return prepareSurveyTitle(
          initialValues[CREATE_SURVEY_FIELD_NAMES.TITLE],
          currentValue,
          prevValue,
          dependantValue
        );
      },
    },
  ],
  validators: [
    {
      type: 'required',
      shouldPassFormValues: true,
      validator: (value, values) =>
        values?.[CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE]
          ? true
          : !isArrayEmpty(value),
    },
  ],
});

const prepareQuestionnaireSwitchField = () => ({
  name: CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE,
  type: SWITCH,
  translationKey: 'questionnaire',
  fieldWrapperClass: 'questionnaire',
  className: 'questionnaireSwitch',
  shouldRemoveLabel: true,
  getTooltipText: (values, labels) =>
    values[CREATE_SURVEY_FIELD_NAMES.HAS_QUESTIONNAIRE]
      ? labels.tooltipTextOn
      : labels.tooltipTextOff,
  dependants: [
    {
      name: CREATE_SURVEY_FIELD_NAMES.ATTRIBUTES,
      resetFnc: (_, isEnabled, dependantValue) =>
        isEnabled ? [] : dependantValue,
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.IS_STATISTICAL,
      resetFnc: (_, isEnabled) => !isEnabled,
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.QUESTIONNAIRE,
      resetFnc: (_, isEnabled, dependantValue) =>
        isEnabled ? EMPTY_QUESTIONNAIRE : dependantValue,
    },
    {
      name: CREATE_SURVEY_FIELD_NAMES.TITLE,
      resetFnc: (_, isEnabled, dependantValue, initialValues, values) => {
        if (!isEnabled) {
          return dependantValue;
        }

        return prepareSurveyTitle(
          initialValues[CREATE_SURVEY_FIELD_NAMES.TITLE],
          [],
          values[CREATE_SURVEY_FIELD_NAMES.ATTRIBUTES],
          dependantValue
        );
      },
    },
  ],
});

const SELF = {
  id: SURVEY_TYPES.SELF,
  icon: SelfAssessmentIcon,
  columns: [
    { ...getMutualColumn() },
    {
      id: 2,
      title: 'People',
      translationKey: 'people',
      columnContentWrapperClass: 'peopleColumnContent',
      columnClass: 'peopleColumn',
      fields: [
        prepareAttributesSelectField(),
        prepareQuestionnaireSwitchField(),
        {
          name: 'people',
          type: SUBTITLE,
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.CREATED_FOR,
          type: PEOPLE_PICKER,
          selectedUsersScrollYClass: 'selectedUsersScrollY',
          translationKey: 'selfReview',
          optionsKey: 'employees',
          hasSelectAll: true,
          required: true,
          showDirectReports: true,
          showSelection: true,
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
      ],
    },
    { ...PREVIEW_COLUMN },
  ],
};

const DIRECT = {
  id: SURVEY_TYPES.DIRECT,
  icon: DirectReviewIcon,
  columns: [
    { ...getMutualColumn() },
    {
      id: 2,
      title: 'People',
      translationKey: 'people',
      columnContentWrapperClass: 'peopleColumnContent',
      columnClass: 'peopleColumn',
      fields: [
        prepareAttributesSelectField(),
        prepareQuestionnaireSwitchField(),
        {
          name: 'people',
          type: SUBTITLE,
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.REVIEWERS,
          type: SELECT,
          translationKey: 'reviewer',
          required: true,
          optionsKey: 'allUsers',
          isUser: true,
          isMulti: true,
          hasDivider: true,
          shouldReturnOption: true,
          circularDependant: CREATE_SURVEY_FIELD_NAMES.CREATED_FOR,
          parser: {
            value: 'id',
            label: 'label',
          },
          isDisabledCheck: values =>
            !isArrayEmpty(values[CREATE_SURVEY_FIELD_NAMES.REVIEWERS]),
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.CREATED_FOR,
          type: SELECT,
          translationKey: 'whoWillBeReviewed',
          required: true,
          optionsKey: 'employees',
          isUser: true,
          isMulti: true,
          hasDivider: true,
          shouldReturnOption: true,
          circularDependant: CREATE_SURVEY_FIELD_NAMES.REVIEWERS,
          parser: {
            value: 'id',
            label: 'label',
          },
          isDisabledCheck: values =>
            !isArrayEmpty(values[CREATE_SURVEY_FIELD_NAMES.CREATED_FOR]),
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
      ],
    },
    { ...PREVIEW_COLUMN },
  ],
};

const TEAM = {
  id: SURVEY_TYPES.TEAM,
  icon: TeamReviewIcon,
  columns: [
    { ...getMutualColumn() },
    {
      id: 2,
      title: 'People',
      translationKey: 'people',
      columnContentWrapperClass: 'peopleColumnContent',
      columnClass: 'peopleColumn',
      fields: [
        prepareAttributesSelectField(),
        prepareQuestionnaireSwitchField(),
        {
          name: 'people',
          type: SUBTITLE,
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.REVIEWERS,
          type: SELECT,
          optionsKey: 'allUsers',
          required: true,
          translationKey: 'reviewer',
          isUser: true,
          isMulti: true,
          hasDivider: true,
          shouldReturnOption: true,
          circularDependant: CREATE_SURVEY_FIELD_NAMES.CREATED_FOR,
          parser: {
            value: 'id',
            label: 'label',
          },
          isDisabledCheck: values =>
            !isArrayEmpty(values[CREATE_SURVEY_FIELD_NAMES.REVIEWERS]),
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.CREATED_FOR,
          type: PEOPLE_PICKER,
          translationKey: 'whoWillBeReviewed',
          selectedUsersScrollYClass: 'selectedUsersScrollY',
          optionsKey: 'employees',
          showSelection: true,
          hasSelectAll: true,
          showDirectReports: true,
          parent: CREATE_SURVEY_FIELD_NAMES.REVIEWERS,
          required: true,
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
      ],
    },
    { ...PREVIEW_COLUMN },
  ],
};

const PEER = {
  id: SURVEY_TYPES.PEER,
  icon: PeerReviewIcon,
  columns: [
    { ...getMutualColumn(true) },
    {
      id: 2,
      title: 'People',
      translationKey: 'people',
      columnContentWrapperClass: 'peopleColumnContent',
      columnClass: 'peopleColumn',
      fields: [
        prepareAttributesSelectField(),
        prepareQuestionnaireSwitchField(),
        {
          name: 'people',
          type: SUBTITLE,
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.CREATED_FOR,
          type: SELECT,
          translationKey: 'whoWillBeReviewed',
          optionsKey: 'employees',
          isSingleSelect: true,
          shouldReturnOption: true,
          required: true,
          isUser: true,
          isMulti: true,
          hasDivider: true,
          circularDependant: CREATE_SURVEY_FIELD_NAMES.REVIEWERS,
          parser: {
            value: 'id',
            label: 'label',
          },
          isDisabledCheck: values =>
            !isArrayEmpty(values[CREATE_SURVEY_FIELD_NAMES.CREATED_FOR]),
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.REVIEWERS,
          type: PEOPLE_PICKER,
          translationKey: 'reviewers',
          selectedUsersScrollYClass: 'selectedUsersScrollY',
          optionsKey: 'allUsers',
          showDirectReports: true,
          hasSelectAll: true,
          showSelection: true,
          parent: CREATE_SURVEY_FIELD_NAMES.CREATED_FOR,
          dependants: [
            {
              name: CREATE_SURVEY_FIELD_NAMES.IS_ANONYMOUS,
              resetFnc: (prevValue, currentValue, dependantValue) => {
                if (currentValue.length < MIN_REVIWERS_FOR_ANONYMOUS_SURVEY) {
                  return false;
                }

                return dependantValue;
              },
            },
          ],
          required: true,
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
      ],
    },
    { ...PREVIEW_COLUMN },
  ],
};

const PULSE = {
  id: SURVEY_TYPES.PULSE,
  icon: CompanyPulseIcon,
  columns: [
    { ...getMutualColumn(true, false) },
    {
      id: 2,
      title: 'People',
      translationKey: 'people',
      columnContentWrapperClass: 'peopleColumnContent',
      columnClass: 'peopleColumn',
      fields: [
        prepareAttributesSelectField(),
        prepareQuestionnaireSwitchField(),
        {
          name: 'people',
          type: SUBTITLE,
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.REVIEWERS,
          type: PEOPLE_PICKER,
          selectedUsersScrollYClass: 'selectedUsersScrollY',
          translationKey: 'reviewers',
          optionsKey: 'allUsers',
          showDirectReports: true,
          hasSelectAll: true,
          required: true,
          showSelection: true,
          hasDivider: true,
          dependants: [
            {
              name: CREATE_SURVEY_FIELD_NAMES.IS_ANONYMOUS,
              resetFnc: (prevValue, currentValue, dependantValue) => {
                if (currentValue.length < MIN_REVIWERS_FOR_ANONYMOUS_SURVEY) {
                  return false;
                }

                return dependantValue;
              },
            },
          ],
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
        {
          content: 'companyAvatar',
          type: READ_ONLY,
          translationKey: 'whoWillBeReviewed',
        },
      ],
    },
    { ...PREVIEW_COLUMN },
  ],
};

const NET_PROMOTER_SCORE = {
  id: SURVEY_TYPES.NPS,
  icon: EnpsIcon,
  columns: [
    { ...getMutualColumn(true, false) },
    {
      id: 2,
      title: 'People',
      translationKey: 'people',
      columnContentWrapperClass: 'peopleColumnContent',
      columnClass: 'peopleColumn',
      fields: [
        {
          name: 'people',
          type: SUBTITLE,
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.REVIEWERS,
          type: PEOPLE_PICKER,
          selectedUsersScrollYClass: 'selectedUsersScrollY',
          translationKey: 'associates',
          optionsKey: 'associates',
          hasSelectAll: true,
          required: true,
          showSelection: true,
          hasDivider: true,
          dependants: [
            {
              name: CREATE_SURVEY_FIELD_NAMES.IS_ANONYMOUS,
              resetFnc: (prevValue, currentValue, dependantValue) => {
                if (currentValue.length < MIN_REVIWERS_FOR_ANONYMOUS_SURVEY) {
                  return false;
                }

                return dependantValue;
              },
            },
          ],
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
      ],
    },
    { ...PREVIEW_COLUMN },
  ],
};

const EMPLOYEE_NET_PROMOTER_SCORE = {
  id: SURVEY_TYPES.ENPS,
  icon: EnpsIcon,
  columns: [
    { ...getMutualColumn(true, false) },
    {
      id: 2,
      title: 'People',
      translationKey: 'people',
      columnContentWrapperClass: 'peopleColumnContent',
      columnClass: 'peopleColumn',
      fields: [
        {
          name: 'people',
          type: SUBTITLE,
        },
        {
          name: CREATE_SURVEY_FIELD_NAMES.REVIEWERS,
          type: PEOPLE_PICKER,
          selectedUsersScrollYClass: 'selectedUsersScrollY',
          translationKey: 'reviewers',
          optionsKey: 'employees',
          showDirectReports: true,
          hasSelectAll: true,
          required: true,
          showSelection: true,
          hasDivider: true,
          dependants: [
            {
              name: CREATE_SURVEY_FIELD_NAMES.IS_ANONYMOUS,
              resetFnc: () => true,
            },
          ],
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
            { type: 'minReviewers', validator: value => value.length >= 6 },
          ],
        },
      ],
    },
    { ...PREVIEW_COLUMN },
  ],
};

export const SURVEY_TYPES_MAP = {
  [SURVEY_TYPES.SELF]: SELF,
  [SURVEY_TYPES.DIRECT]: DIRECT,
  [SURVEY_TYPES.TEAM]: TEAM,
  [SURVEY_TYPES.PEER]: PEER,
  [SURVEY_TYPES.PULSE]: PULSE,
  [SURVEY_TYPES.ENPS]: EMPLOYEE_NET_PROMOTER_SCORE,
  [SURVEY_TYPES.NPS]: NET_PROMOTER_SCORE,
};

export const SURVEY_TYPES_DIALOG_MAP = {
  [SURVEY_TYPES.SELF]: SELF,
  [SURVEY_TYPES.DIRECT]: DIRECT,
  [SURVEY_TYPES.TEAM]: TEAM,
  [SURVEY_TYPES.PEER]: PEER,
  [SURVEY_TYPES.PULSE]: PULSE,
  [SURVEY_TYPES.NPS_ENPS]: EMPLOYEE_NET_PROMOTER_SCORE,
};

export const SURVEY_TYPE_FILTER_OPTIONS = [
  {
    label: 'Team & Peer & Direct',
    value: [SURVEY_TYPES.TEAM, SURVEY_TYPES.PEER, SURVEY_TYPES.DIRECT],
    translationKey: 'teamPeerDirect',
  },
  {
    label: 'Team & Peer',
    value: [SURVEY_TYPES.TEAM, SURVEY_TYPES.PEER],
    translationKey: 'teamPeer',
  },
  {
    label: 'Team & Direct',
    value: [SURVEY_TYPES.TEAM, SURVEY_TYPES.DIRECT],
    translationKey: 'teamDirect',
  },
  {
    label: 'Peer & Direct',
    value: [SURVEY_TYPES.PEER, SURVEY_TYPES.DIRECT],
    translationKey: 'peerDirect',
  },
  {
    label: 'Team',
    value: [SURVEY_TYPES.TEAM],
    translationKey: 'team',
  },
  {
    label: 'Peer',
    value: [SURVEY_TYPES.PEER],
    translationKey: 'peer',
  },
  {
    label: 'Direct',
    value: [SURVEY_TYPES.DIRECT],
    translationKey: 'direct',
  },
  {
    label: 'Self',
    value: [SURVEY_TYPES.SELF],
    translationKey: 'self',
  },
  {
    label: 'Pulse',
    value: [SURVEY_TYPES.PULSE],
    translationKey: 'pulse',
  },
];

export const INITIAL_SURVEY_TYPE_FILTER_VALUE = SURVEY_TYPE_FILTER_OPTIONS[0];

export const SURVEY_VIEW_OPTIONS = [
  {
    value: false,
    name: 'List',
    key: 'list',
  },
  {
    value: true,
    name: 'Group',
    key: 'group',
  },
];

export const SURVEY_GROUP_FIELD = {
  name: 'Group',
  type: FIELD_TYPES.SELECT,
  translationKey: 'group',
  required: true,
  parser: {
    label: 'title',
    value: 'id',
  },
  validators: [{ type: 'required', validator: isEmpty }],
};

export const SURVEY_TITLE_FIELD = {
  name: 'title',
  type: FIELD_TYPES.TEXT,
  translationKey: 'name',
  required: true,
  validators: [
    { type: 'required', validator: isEmpty },
    { type: 'forbiddenCharacters', validator: forbiddenCharacters },
    {
      type: 'maxLength',
      validator: value => validateLength(value, 0, 150),
    },
  ],
};

export const INIT_SORT = {
  column: 'sentAt',
  asc: false,
  sortKey: '-sent_at',
};

export const SURVEYS_GROUP_FIELD = {
  name: 'title',
  type: FIELD_TYPES.TEXT,
  translationKey: 'name',
  required: true,
  validators: [
    { type: 'required', validator: isEmpty },
    { type: 'forbiddenCharacters', validator: forbiddenCharacters },
    {
      type: 'maxLength',
      validator: value => validateLength(value, 0, 500),
    },
    {
      type: 'availableTitle',
      async: true,
      validator: value => {
        const params = {
          title: trimString(value),
        };
        return http
          .get(API_SURVEY_GROUPS_BASIC, { params })
          .then(({ data }) => isArrayEmpty(data));
      },
    },
  ],
};
